import { AssessmentHistory, ProductResult } from '@shared/api/types';
import SimpleLineChart from '@shared/components/chart/simple-line-chart';
import { CustomTooltipProps } from '@shared/components/chart/tooltips/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import SimpleSelect from '@shared/components/simple-select';
import { ChartConfig } from '@shared/components/ui/chart';
import { cn, formatNumber, formatSmallNumber } from '@shared/lib/utils';
import React from 'react';

interface ProductDetailEmissionsOverTimeProps
  extends React.HTMLAttributes<HTMLDivElement> {
  productResult: ProductResult;
}

const carbonIntensityConfig: ChartConfig = {
  carbonIntensity: {
    label: 'Carbon Intensity',
    color: 'var(--chart-carbon-intensity)',
    unit: 'kgCO2e/kg',
  },
};

const totalEmissionsConfig: ChartConfig = {
  totalEmissions: {
    label: 'Total Emissions',
    color: 'var(--chart-3)',
    unit: 'tCO2e',
  },
};

const toDay = (date: string) => new Date(date).toISOString().split('T')[0];

export function ProductDetailEmissionsOverTime({
  className,
  productResult,
}: ProductDetailEmissionsOverTimeProps) {
  const [selectedMetric, setSelectedMetric] = React.useState<
    'Carbon intensity' | 'Total emissions'
  >('Carbon intensity');

  const currentDate = React.useMemo(
    () => new Date().toISOString().split('T')[0],
    []
  );

  // Sort oldest-first, then deduplicate keeping the latest assessment per day
  const sortedHistory = React.useMemo(() => {
    const sorted = [...productResult.assessmentHistory].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
    return sorted.filter(
      (item, idx, arr) =>
        idx === arr.findLastIndex((d) => toDay(d.date) === toDay(item.date))
    );
  }, [productResult.assessmentHistory]);

  // Add current date as the final point with same value as last assessment.
  // Each point also gets a numeric `timestamp` (ms) so the XAxis can use a
  // linear scale and space points proportionally to real time intervals.
  const chartData = React.useMemo(() => {
    const withTs = sortedHistory.map((d) => ({
      ...d,
      timestamp: new Date(d.date).getTime(),
    }));
    const last = withTs[withTs.length - 1];
    if (!last) return withTs;
    return [
      ...withTs,
      {
        ...last,
        date: currentDate,
        timestamp: Date.now(), // use exact current moment so it's always rightmost
        _current: true as const,
      },
    ];
  }, [sortedHistory, currentDate]);

  const xDomain = React.useMemo(() => {
    if (!chartData.length) return [0, 1];
    const ts = chartData.map((d) => d.timestamp as number);
    return [Math.min(...ts), Math.max(...ts)];
  }, [chartData]);

  // Calculate dynamic domain with padding based on selected metric
  const isCarbonIntensity = selectedMetric === 'Carbon intensity';
  const values = chartData
    .map((d) => (isCarbonIntensity ? d.carbonIntensity : d.totalEmissions))
    .filter((v): v is number => v != null);
  const minValue = values.length ? Math.min(...values) : 0;
  const maxValue = values.length ? Math.max(...values) : 1;
  const padding = (maxValue - minValue) * 0.5 || 0.5;
  const domain = [Math.max(0, minValue - padding), maxValue + padding];

  const chartConfig = isCarbonIntensity
    ? carbonIntensityConfig
    : totalEmissionsConfig;

  const EmissionsTooltip = React.useMemo(
    () =>
      function Tooltip({ value, name, item }: CustomTooltipProps) {
        const payload = item.payload as AssessmentHistory & {
          _current?: boolean;
        };
        const isCurrent = payload._current === true;

        const idx = chartData.findIndex((d) => d.uuid === payload.uuid);
        const prevPoint = idx > 0 ? chartData[idx - 1] : null;
        const prevValue: number | null = prevPoint
          ? isCarbonIntensity
            ? prevPoint.carbonIntensity
            : prevPoint.totalEmissions
          : null;

        const numValue = value as number;
        const percentChange =
          !isCurrent && prevValue != null && prevValue !== 0
            ? ((numValue - prevValue) / Math.abs(prevValue)) * 100
            : null;

        const formattedDate = isCurrent
          ? 'Today'
          : new Date(payload.date).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            });

        const unitVariant = isCarbonIntensity ? 'kgCO2e/kg' : 'tCO2e';

        return (
          <div>
            <span>{formattedDate}</span>
            <div className="mt-1 flex flex-1 items-center gap-2 gap-y-4">
              <div
                className="size-2.5 shrink-0 rounded-[2px] bg-[--color-bg]"
                style={
                  {
                    '--color-bg': `var(--color-${name})`,
                  } as React.CSSProperties
                }
              />
              <div className="flex items-center gap-0.5 font-mono font-medium tabular-nums text-foreground">
                {numValue != null &&
                  (isCarbonIntensity
                    ? formatSmallNumber(numValue)
                    : formatNumber(numValue))}
                <Unit variant={unitVariant} className="ml-1" />
                {percentChange !== null && (
                  <span
                    className={cn(
                      'ml-1 flex items-center text-xs font-normal',
                      percentChange < 0
                        ? 'text-secondary-600'
                        : 'text-error-500'
                    )}
                  >
                    {percentChange < 0 ? (
                      <Icons.chevronDown size={10} className="p-0" />
                    ) : (
                      <Icons.chevronUp size={10} className="p-0" />
                    )}
                    {Math.abs(percentChange).toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      },
    [chartData, isCarbonIntensity]
  );

  return (
    <div
      className={cn(
        'rounded-md  bg-gray-00 px-3 py-3 max-h-60 h-56',
        className
      )}
    >
      <div className="flex items-center justify-between">
        <Text variant="cardTitle" className="text-nowrap text-primary-900">
          Emissions over time
        </Text>
        <SimpleSelect
          triggerClassName="border-none h-min mr-0 pr-0 py-0 my-0 outline-none focus:ring-0 text-sm text-gray-500"
          value={selectedMetric}
          options={['Total emissions', 'Carbon intensity']}
          onValueChange={(value) =>
            setSelectedMetric(value as 'Carbon intensity' | 'Total emissions')
          }
        />
      </div>

      <SimpleLineChart
        chartConfig={chartConfig}
        chartData={chartData}
        nameKey="date"
        dataKey={
          selectedMetric === 'Carbon intensity'
            ? 'carbonIntensity'
            : 'totalEmissions'
        }
        XAxisProps={{
          tick: false,
          axisLine: true,
          dataKey: 'timestamp',
          type: 'number',
          domain: xDomain,
        }}
        YAxisProps={{ tick: false, axisLine: true, domain }}
        showGrid={false}
        lineType="stepAfter"
        margin={{ left: -50, bottom: 10, right: 10, top: 20 }}
        className="size-full"
        hideTooltipLabel
        tooltipFormatter={EmissionsTooltip}
      />
    </div>
  );
}
